// src/components/Layout.js
import { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  useTheme, 
  Typography, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';
import CurrencyToggle from './CurrencyToggle';

export default function Layout({ children, variant = 'default' }) {
  const theme = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'Meeting Calculator', path: '/meetings' },
    { label: 'Live Meeting Timer', path: '/live-meetings' },
    { label: 'Documents', path: '/documents' },
    { label: 'How It Works', path: '/methodology' },
  ];

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Manage body background color based on variant
  useEffect(() => {
    const bodyBackgroundColor = variant === 'calculator' 
      ? (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8f9fb')
      : (theme.palette.mode === 'dark' ? theme.palette.background.default : '#ffffff');
    
    document.body.style.backgroundColor = bodyBackgroundColor;
    
    // Cleanup function to reset body background when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [variant, theme.palette.mode, theme.palette.background.default]);
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Skip Links for Keyboard Navigation */}
      <Box
        sx={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 9999,
          '&:focus-within': {
            left: '10px',
            top: '10px',
          },
        }}
      >
        <Button
          href="#main-content"
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:focus': {
              position: 'relative',
              left: 0,
              top: 0,
            },
          }}
        >
          Skip to main content
        </Button>
      </Box>
      
      <AppBar
        position="static"
        elevation={1}
        component="header"
        role="banner"
        sx={{ 
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          boxShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(255,255,255,0.1)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          {/* Branded Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
            <Typography sx={{ fontSize: '1.8rem' }}>💸</Typography>
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 800,
                color: 'text.primary',
                textDecoration: 'none',
                fontSize: '1.5rem',
              }}
            >
              cost.wtf
            </Typography>
          </Box>
          
          {/* Desktop Navigation */}
          {!isMobile && (
            <Box 
              component="nav" 
              role="navigation" 
              aria-label="Main navigation"
              sx={{ display: 'flex', gap: 2, justifyContent: 'center', flex: 1 }}
            >
              {navigationItems.map((item) => (
                <Button 
                  key={item.path}
                  component={Link} 
                  to={item.path} 
                  sx={{ color: theme.palette.text.primary }}
                  aria-label={`Navigate to ${item.label}`}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Mobile Navigation */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleMobileMenuToggle}
                sx={{ color: theme.palette.text.primary }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Currency Toggle - More prominent positioning */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2, mr: 2, flexDirection: 'column', gap: 0.5 }}>

              <CurrencyToggle />
            </Box>
          )}
          
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mr: 1 }}>
              <CurrencyToggle />
            </Box>
          )}

          {/* Dark Mode Toggle - Right side */}
          <Box sx={{ ml: 'auto' }}>
            <DarkModeToggle />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation menu"
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '1.5rem' }}>💸</Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 800,
                color: 'text.primary',
                fontSize: '1.3rem',
              }}
            >
              cost.wtf
            </Typography>
          </Box>
          <IconButton 
            onClick={handleMobileMenuClose} 
            sx={{ color: theme.palette.text.primary }}
            aria-label="Close navigation menu"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <List 
          component="nav" 
          role="navigation" 
          aria-label="Mobile navigation links"
        >
          {navigationItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={handleMobileMenuClose}
                aria-label={`Navigate to ${item.label}`}
                sx={{
                  py: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemText 
                  primary={item.label}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        {/* Currency Selection Section in Mobile */}
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              mb: 1,
              textAlign: 'center',
              fontSize: '0.875rem',
            }}
          >
            Currency & Region
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CurrencyToggle />
          </Box>
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'center' }}>
          <DarkModeToggle />
        </Box>
      </Drawer>

      <Box 
        component="main" 
        id="main-content"
        sx={{ 
          padding: '2rem', 
          maxWidth: '800px', 
          margin: '1px auto 0',
          backgroundColor: variant === 'calculator' 
            ? (theme.palette.mode === 'dark' ? '#1a1a1a' : '#f8f9fb')
            : theme.palette.background.default,
          minHeight: 'calc(100vh - 160px)', // Account for AppBar and footer height
          flexGrow: 1
        }}
      >
        {children}
      </Box>
      
      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 3, 
          textAlign: 'center', 
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          mt: 'auto'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            mb: 1,
          }}
        >
          Made with ❤️ for better meetings
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
          }}
        >
          © 2025 cost.wtf - Stop wasting money on meetings
        </Typography>
      </Box>
    </Box>
  );
}
